import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Button, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { formAPI } from 'api/form';
import { useAppBuilderContext } from 'app/app/editor/context';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { FormTabType } from 'components/common/form/tab';
import { useFormState } from 'react-hook-form';
import { IComponentNode, processSettingsDefaults, processSettingsVariables } from 'app/app/editor/index.functions';
import { FormDataAPIType, FormRendererProps, RawFormComponentType } from 'components/common/form';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { FormComponentValue } from 'components/common/form/layout/control';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { formSliceState } from 'store/formSlice';

export default function SettingsForm({ node }: { node: IComponentNode }) {
    const ctx = useAppBuilderContext();
    const { t } = useBundleTranslation();
    const componentUid = node.params.uid;
    const component = ctx.page?.components[node.params.uid];

    const { status, data } = useQuery<any, Error>({
        queryKey: ['app-builder-component-form-json', component?.component ?? componentUid],
        queryFn: () => {
            if (!component) return null;
            return formAPI.load('/data/app/components/' + component.component + '/form.json');
        },
    });

    if (status === 'pending') {
        return <Box>{t('loading___')}</Box>;
    } else if (status === 'error') {
        return <Box>{t('error___')}</Box>;
    }

    return data ? <SettingsFormComponent node={node} data={data} /> : null;
}

function SettingsFormComponent({ node, data }: { node: IComponentNode; data: any }) {
    const ctx = useAppBuilderContext();
    const [page, setPage, sendMessage] = [ctx.page, ctx.setPage, ctx.sendMessage];

    const componentUid = node.params.uid;
    const component = page?.components[node.params.uid];

    const [initialFormState, setInitialFormState] = useState(processSettingsDefaults(component, data));
    // If selected Node changed => update form default state
    useEffect(() => {
        setInitialFormState(processSettingsDefaults(component, data));
    }, [componentUid, component, data]);
    useEffect(() => {
        simpleForm.form.hookFormReset(initialFormState);
    }, [initialFormState]);

    const formSettings: { tabs: FormTabType[] } = useMemo(() => {
        const tabs: Array<FormTabType> = [];
        if (data?.data?.settings) {
            return data.data.settings;
        }
        return { tabs: tabs };
    }, [data?.data?.settings]);

    const simpleForm = useCustomSimplifiedForm(initialFormState, {
        settingsUrl: '/data/app/select-data',
        formSettings: formSettings,
    } as FormRendererProps);

    const { dirtyFields } = useFormState({ control: simpleForm.form.hookFormControl });
    const isDirty = Object.entries(dirtyFields).length > 0;

    if (!component) {
        return null;
    }

    const cancelClick = () => {
        simpleForm.form.hookFormReset();
    };

    const applySettings = () => {
        const formData = simpleForm.form.hookFormGetValues();
        simpleForm.form.hookFormReset(formData);

        let newPage = structuredClone(page);
        if (data.data?.fields) {
            let ret = processSettingsVariables(data.data?.fields, formData);
            newPage.components[componentUid].settings = ret.settings;
            newPage.components[componentUid].variables = ret.variables;
        } else newPage.components[componentUid].settings = formData;

        setPage(newPage, false);
        sendMessage({ name: 'updateComponent', data: { uid: componentUid, updateType: 'settings' }, broadcast: false });
    };

    return (
        <>
            <Stack spacing={1} sx={{ p: 1.5 }}>
                {formSettings.tabs.map((tab: FormTabType) =>
                    tab.components.map((c) => (
                        <FormComponent
                            componentType={page.components[componentUid].component}
                            key={`${tab.name}_${c.name}`}
                            componentUid={componentUid}
                            component={c}
                            form={simpleForm}
                        />
                    )),
                )}
            </Stack>
            {isDirty && (
                <Stack
                    spacing={1}
                    direction={'row'}
                    sx={{
                        borderTop: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        p: 1.5,
                        mt: 1.5,
                        overflowX: 'clip',
                        justifyContent: 'flex-start',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.default',
                    }}
                >
                    <Button onClick={() => applySettings()}>{'Apply'}</Button>
                    <Button variant={'outlined'} color="neutral" onClick={() => cancelClick()}>
                        {'Cancel'}
                    </Button>
                </Stack>
            )}
        </>
    );
}

function FormComponent({
    form,
    component,
    componentUid,
    componentType,
}: {
    form: FormDataAPIType;
    component: RawFormComponentType;
    componentUid: string;
    componentType: string;
}) {
    const { t } = useBundleTranslation();

    const asyncDataUrl = useMemo(() => {
        if (!component.asyncDataUrl) {
            return '';
        }
        if (component?.props?.urlParams) {
            const queryString = new URLSearchParams(component.props.urlParams).toString();
            return `${component.asyncDataUrl}${component.asyncDataUrl?.includes('?') ? '&' : '?'}${queryString}`;
        }
        return component.asyncDataUrl;
    }, [component?.asyncDataUrl, component?.props?.urlParams]);

    const { status, data } = useQuery<Array<FormComponentValue>, Error>({
        queryKey: ['app-builder-component-data', component.component, asyncDataUrl],
        queryFn: async () => {
            const defaultResult =
                componentType == 'filter' && component.component == 'FormSelect' ? [{ label: '--', value: '' }] : [];
            if (!asyncDataUrl) {
                return defaultResult;
            }
            const response: AxiosResponse<APIResponse<Array<FormComponentValue>>> = await instance.get(asyncDataUrl);
            return response.data.status == 'OK' ? defaultResult.concat(response.data.data) : defaultResult;
        },
    });
    const ctx = useAppBuilderContext();
    const [pageInternalName, pageSession, isTemplate] = [ctx.pageInternalName, ctx.pageSession, ctx.isTemplate];

    // Fix for async Select Data
    const formKey = form.form.formKey;
    useEffect(() => {
        if (data) {
            emptyTmpValues.current = data;
        }
    }, [data]);

    const emptyTmpValues = useRef(component.data ?? data);
    const tmpValues: any = useSelector((state: any) => {
        const form = (state?.form as formSliceState).formElementsValues[formKey];
        if (form && typeof form[component.name] != 'undefined') {
            return form[component.name];
        }
        return emptyTmpValues.current;
    });
    const [componentStartValues, setComponentStartValues] = useState(tmpValues);
    useEffect(() => {
        if (JSON.stringify(componentStartValues) == JSON.stringify(tmpValues)) {
            return;
        }
        setComponentStartValues(tmpValues);
    }, [tmpValues]);

    if (status != 'success') {
        return <Box>{t('loading___')}</Box>;
    }

    const urlAdd = [
        '?pageInternalName=',
        pageInternalName,
        '&pageSession=',
        pageSession,
        '&componentUid=',
        componentUid,
        '&componentSetting=',
        component.name,
        '&isTemplate=',
        isTemplate ? 'Y' : 'N',
    ].join('');

    if (['UploadImage', 'UploadFile'].includes(component.component)) {
        if (undefined === component?.props) component.props = {};
        component.props['ajaxApiUrl'] = '/data/app/upload-asset' + urlAdd;
        component.props['ajaxApiRemoveUrl'] = '/data/app/remove-asset' + urlAdd;

        component.props['onAjaxUploadResult'] = (response: any) => {
            if (response.data.status === 'OK') {
                form.form.hookFormSetValue(component.name, response.data.relative_path);
            }
        };
    }

    if (['PageVariables'].includes(component.component)) {
        component.props['uploadAssetUrl'] = '/data/app/upload-asset' + urlAdd;
    }

    return (
        <TemplateFormComponent
            childComponentsRender={(form: FormDataAPIType, component: RawFormComponentType) => {
                return (
                    <FormComponent
                        form={form}
                        component={component}
                        componentUid={componentUid}
                        componentType={componentType}
                    />
                );
            }}
            elementProps={form}
            component={component.component}
            components={component?.components}
            componentValues={componentStartValues}
            name={component.name}
            props={{
                label: component.label ?? component.name,
                asyncDataUrl: component?.asyncDataUrl,
                ...(component?.props ?? {}),
            }}
            deps={component.deps ?? []}
            wrapper={true}
            designVariant={'secondary'}
        />
    );
}
